import { RefObject, useEffect, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import {
    registerPanelQuickReplacementCancelSelectionHandler,
    registerPanelQuickReplacementDirectionHandler
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/quickPanelReplacementController';
import {
    buildEdgeHandles,
    getNextPanel,
    getScreenEdge,
    getSelectedPanel,
    isRotation90Degrees,
    replacePanel
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/utils/quickPanelReplacementUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type {
    EdgeHandle,
    MousePreview,
    PanelReplacementDirection,
    PanelReplacementGrid
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/types/quickPanelReplacementTypes';
import type { Camera } from 'three';

interface ReplacementLifecyclehHandlerProps {
    isOpen: boolean;
    isQueryLoading: boolean;
    isQueryError: boolean;
    grid: PanelReplacementGrid;
    camera: Camera;
    activeEdge: EdgeHandle | null;
    mousePreview: MousePreview | null;
    viewRevision: number;
    mouseEdgeRef: RefObject<EdgeHandle | null>;
    previewRef: RefObject<MousePreview | null>;
    canvasRef: RefObject<HTMLCanvasElement | null>;
    initialCursorRef: RefObject<string>;
    setActiveEdge: Dispatch<SetStateAction<EdgeHandle | null>>;
    setMousePreview: Dispatch<SetStateAction<MousePreview | null>>;
    invalidate: () => void;
}

export const useReplacementLifecycleHandler = ({
    isOpen,
    isQueryLoading,
    isQueryError,
    grid,
    camera,
    activeEdge,
    mousePreview,
    viewRevision,
    mouseEdgeRef,
    previewRef,
    canvasRef,
    initialCursorRef,
    setActiveEdge,
    setMousePreview,
    invalidate
}: ReplacementLifecyclehHandlerProps) => {
    const { t } = useTranslation();
    const setIsPanelQuickReplacementGridActive = usePlacementStore(
        (state) => state.setIsPanelQuickReplacementGridActive
    );

    useEffect(() => {
        setIsPanelQuickReplacementGridActive(isOpen && activeEdge !== null);
    }, [activeEdge, isOpen, setIsPanelQuickReplacementGridActive]);

    useEffect(() => {
        invalidate();
    }, [activeEdge, invalidate, mousePreview, viewRevision]);

    useEffect(() => {
        if (isOpen && isQueryError) {
            toastNotifications.error({ title: t('error'), message: t('errorLoadingPanelData') });
        }
    }, [isOpen, isQueryError, t]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        return registerPanelQuickReplacementCancelSelectionHandler(() => {
            if (!mouseEdgeRef.current) {
                return false;
            }
            mouseEdgeRef.current = null;
            previewRef.current = null;
            setActiveEdge(null);
            setMousePreview(null);
            if (canvasRef.current) {
                canvasRef.current.style.cursor = initialCursorRef.current;
            }
            return true;
        });
    }, [canvasRef, initialCursorRef, isOpen, mouseEdgeRef, previewRef, setActiveEdge, setMousePreview]);

    useEffect(() => {
        if (!isOpen || isQueryLoading) {
            return;
        }
        return registerPanelQuickReplacementDirectionHandler((direction: PanelReplacementDirection, shrink) => {
            const state = usePlacementStore.getState();
            const currentSelection = getSelectedPanel(state, grid);
            const currentAnchorPart = currentSelection
                ? currentSelection.instance.parts[Object.keys(currentSelection.instance.parts)[0]]
                : undefined;
            if (!currentSelection || !currentAnchorPart) {
                return false;
            }

            const currentEdgeHandles = buildEdgeHandles(currentSelection.item, currentAnchorPart);
            const edge = getScreenEdge(currentEdgeHandles, camera, direction);

            const target = getNextPanel(
                grid,
                currentSelection.item,
                edge.dimension,
                shrink ? -1 : 1,
                isRotation90Degrees(currentAnchorPart.rotation),
                Object.keys(currentSelection.instance.parts).length
            );
            return target ? replacePanel(state, currentSelection, target, edge) : false;
        });
    }, [camera, grid, isOpen, isQueryLoading]);
};
